/**
 * SnapshotPreviewColumn — snapshot preview panel on right side of Explorer
 *
 * Renders as a column between Explorer/Sidebar and main Editor area.
 * When snapshot is selected in Explorer, this column displays snapshot content/diff.
 * Resizable width with drag handle on the right edge.
 */

import { useCallback, useRef, useState } from "react";
import { useEditorStore } from "../store/editor-store";
import { SnapshotPreviewPanel } from "./preview/SnapshotPreviewPanel";

const MIN_COLUMN_WIDTH = 240;
const MAX_COLUMN_WIDTH = 600;

export function SnapshotPreviewColumn() {
    const snapshotPreviewState = useEditorStore((s) => s.snapshotPreviewState);
    const snapshotColumnWidth = useEditorStore((s) => s.snapshotColumnWidth);
    const setSnapshotColumnWidth = useEditorStore((s) => s.setSnapshotColumnWidth);

    const isDraggingRef = useRef(false);
    const [isHoveringResize, setIsHoveringResize] = useState(false);

    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            isDraggingRef.current = true;
            const startX = e.clientX;
            const startWidth = snapshotColumnWidth;
            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";

            const handleMouseMove = (ev: MouseEvent) => {
                if (!isDraggingRef.current) return;
                const delta = ev.clientX - startX;
                const newWidth = Math.min(
                    MAX_COLUMN_WIDTH,
                    Math.max(MIN_COLUMN_WIDTH, startWidth + delta),
                );
                setSnapshotColumnWidth(newWidth);
            };

            const handleMouseUp = () => {
                if (!isDraggingRef.current) return;
                isDraggingRef.current = false;
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };
        },
        [snapshotColumnWidth, setSnapshotColumnWidth],
    );

    if (!snapshotPreviewState.enabled) {
        return null;
    }

    return (
        <div
            className="flex flex-col border-r border-border bg-background shrink-0 overflow-hidden relative"
            style={{ width: `${snapshotColumnWidth}px` }}
        >
            {/* Content */}
            <div className="flex-1 overflow-hidden">
                <SnapshotPreviewPanel />
            </div>

            {/* Resize handle on right edge */}
            <div
                onMouseDown={handleMouseDown}
                onMouseEnter={() => setIsHoveringResize(true)}
                onMouseLeave={() => setIsHoveringResize(false)}
                className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/30 active:bg-primary/50 transition-colors z-10"
                style={{
                    backgroundColor: isHoveringResize ? "rgb(99, 102, 241 / 0.3)" : "transparent",
                }}
            />
        </div>
    );
}
